
import React, { useState } from 'react';
import { type SurgeryFull } from '../types';

type Props = {
  surgeries: SurgeryFull[];
};

const YAS_LABELS: Record<string, string> = {
  '1': '18-35',
  '2': '36-50',
  '3': '51-65',
  '4': '66+',
};

const SEKER_LABELS: Record<string, string> = {
  '0': 'Normal',
  '1': 'Yüksek',
  '2': 'Çok Yüksek',
};

const SIGARA_LABELS: Record<string, string> = {
  '0': 'Yok',
  '1': 'Az',
  '2': 'Çok',
};

export default function SurgeriesTable({ surgeries }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const totalPages = Math.ceil(surgeries.length / perPage);
  const paginated = surgeries.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <i className="ri-file-list-3-line text-teal-600"></i>
          Ameliyat Kayıtları ({surgeries.length})
        </h3>
      </div>

      {surgeries.length === 0 ? (
        <div className="p-12 text-center text-gray-500">
          <i className="ri-inbox-line text-4xl mb-2 block"></i>
          Kayıt bulunamadı
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="w-8 py-3 px-3"></th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider">Hasta</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider">Klinik</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider">Tarih</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider">Yaş</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider">Cinsiyet</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider">Şeker</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider">Sigara</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider">İmplant</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((surgery) => {
                  const isExpanded = expandedId === surgery.id;
                  const failCount = surgery.implants.filter((t) => t.failed === true).length;

                  return (
                    <React.Fragment key={surgery.id}>
                      <tr
                        onClick={() => setExpandedId(isExpanded ? null : surgery.id)}
                        className={`border-b border-gray-50 cursor-pointer transition-all ${
                          isExpanded ? 'bg-teal-50/50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="py-3 px-3 text-center">
                          <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-gray-400`}></i>
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900 text-sm">
                          {surgery.patient_name || 'Adsız'}
                        </td>
                        <td className="py-3 px-4 text-gray-600 text-sm">
                          {surgery.clinic || '—'}
                        </td>
                        <td className="py-3 px-4 text-gray-700">
                          {new Date(surgery.operation_date).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="py-3 px-4 text-center text-gray-700">
                          {YAS_LABELS[surgery.yas] || surgery.yas}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            surgery.cinsiyet === 'e'
                              ? 'bg-sky-50 text-sky-700'
                              : 'bg-pink-50 text-pink-700'
                          }`}>
                            {surgery.cinsiyet === 'e' ? 'Erkek' : 'Kadın'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`text-xs font-medium ${
                            surgery.seker === '2'
                              ? 'text-red-600'
                              : surgery.seker === '1'
                              ? 'text-amber-600'
                              : 'text-green-600'
                          }`}>
                            {SEKER_LABELS[surgery.seker] || surgery.seker}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            surgery.sigara !== '0'
                              ? 'bg-orange-50 text-orange-700'
                              : 'bg-green-50 text-green-700'
                          }`}>
                            {SIGARA_LABELS[surgery.sigara] || surgery.sigara}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-xs font-semibold">
                              {surgery.implants.length}
                            </span>
                            {failCount > 0 && (
                              <span className="px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs font-semibold">
                                {failCount} fail
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isExpanded && surgery.implants.length > 0 && (
                        <tr key={`${surgery.id}-detail`}>
                          <td colSpan={9} className="bg-gray-50/80 px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {surgery.implants.map((tooth, idx) => (
                                <div
                                  key={idx}
                                  className={`p-4 rounded-lg border ${
                                    tooth.failed
                                      ? 'bg-red-50/50 border-red-200'
                                      : 'bg-white border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-gray-900 text-sm">
                                      Diş #{tooth.tooth_number}
                                    </span>
                                    {tooth.failed && (
                                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded font-medium whitespace-nowrap">
                                        Fail
                                      </span>
                                    )}
                                  </div>
                                  <div className="space-y-1 text-xs text-gray-600">
                                    <p><strong>Marka:</strong> {tooth.implant_brand || '—'}</p>
                                    <p><strong>Kemik:</strong> Tip {tooth.kemik}</p>
                                    <p><strong>Soket:</strong> {tooth.soket === 'e' ? 'Taze Çekim' : 'Normal'}</p>
                                    <p><strong>Greft:</strong> {tooth.greft === 'e' ? 'Evet' : 'Hayır'}</p>
                                    <p><strong>Hacim:</strong> {tooth.hacim === 'z' ? 'Yeterli' : 'Yetersiz'}</p>
                                    <p><strong>VDK:</strong> {tooth.vdk}</p>
                                    {tooth.failed && tooth.fail_date && (
                                      <p><strong>Fail Tarihi:</strong> {new Date(tooth.fail_date).toLocaleDateString('tr-TR')}</p>
                                    )}
                                    {tooth.failed && tooth.fail_days != null && (
                                      <p><strong>Fail Süresi:</strong> {tooth.fail_days} gün</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {(currentPage - 1) * perPage + 1}-
                {Math.min(currentPage * perPage, surgeries.length)} / {surgeries.length} kayıt
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>

                {Array.from(
                  { length: Math.min(totalPages, 5) },
                  (_, i) => {
                    let page: number;
                    if (totalPages <= 5) { page = i + 1; }
                    else if (currentPage <= 3) { page = i + 1; }
                    else if (currentPage >= totalPages - 2) { page = totalPages - 4 + i; }
                    else { page = currentPage - 2 + i; }
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium cursor-pointer ${
                          currentPage === page
                            ? 'bg-teal-500 text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }
                )}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
